import { isProPlan, planLabel } from '../lib/plans';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, X, Award } from 'lucide-react';
import { formatPlanDate } from './PlanBadges';
import { VERSION_LABELS } from '../lib/entitlements';

const API = process.env.REACT_APP_BACKEND_URL;

// A6: yeni elle satış standardı 12 takvim ayı (venue_paid_12m). Eski 6 aylık kayıtlar sürüm etiketiyle görünür,
// süreleri değişmez; 6 aylık paket yeni satışa kapalıdır. Takvim günü her zaman Europe/Istanbul'dur (sunucu kuralı);
// tarayıcı başka saat diliminde olsa da bugün ve bitiş günü İstanbul'a göre hesaplanır ve +03:00 ile gönderilir.
const istanbulToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date()).reduce((out, part) => ({ ...out, [part.type]: part.value }), {});
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
};
export const monthsFromToday = months => {
  const { year, month, day } = istanbulToday();
  const total = year * 12 + (month - 1) + months, endYear = Math.floor(total / 12), endMonth = total % 12 + 1;
  const lastDay = new Date(Date.UTC(endYear, endMonth, 0)).getUTCDate();
  return `${endYear}-${String(endMonth).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
};
// Gün sonu İstanbul saatiyle (Türkiye yıl boyu UTC+03:00).
export const istanbulEndOfDay = date => `${date}T23:59:59+03:00`;
const istanbulDate = value => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value).slice(0, 10)
    : new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(parsed);
};
const detailText = (err, fallback) => err.response?.data?.detail?.message || (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : fallback);

// İlk 50 kurucu mekân rozeti: yalnız sahip kilidi olan ana yönetici görür ve kullanır (sunucu da 403 verir).
export const FounderBadgeControl = ({ userId, number = null, testId, onChanged }) => {
  const [info, setInfo] = useState(null), [choice, setChoice] = useState(''), [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const load = async (keepError = false) => {
    try { const { data } = await axios.get(`${API}/api/admin/founder-badges`, { timeout: 15000 }); setInfo(data); if (!keepError) setError(''); }
    catch (err) { setError(detailText(err, 'Kurucu rozetleri yüklenemedi.')); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const current = number || info?.items?.find(item => item.user_id === userId && item.status === 'active')?.number || null;
  const assign = async () => {
    if (!choice) return setError('Boştaki numaralardan birini seç.');
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/admin/founder-badges`, { number: Number(choice), user_id: userId });
      toast.success(`${data.label} verildi. Rozet ücretli hak vermez; ücretli üyelik ayrıca yönetilir.`);
      onChanged?.({ owner_founder_number: data.number }); setChoice(''); await load();
    } catch (err) { setError(detailText(err, 'Kurucu rozeti verilemedi.')); await load(true); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (reason.trim().length < 3) return setError('Geri alma gerekçesini yaz (en az 3 karakter).');
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/api/admin/founder-badges/${current}/revoke`, { reason: reason.trim() });
      toast.success(`Kurucu Mekân #${current} geri alındı.`); onChanged?.({ owner_founder_number: null }); setReason(''); await load();
    } catch (err) { setError(detailText(err, 'Kurucu rozeti geri alınamadı.')); }
    finally { setBusy(false); }
  };
  return <div className="w-full flex flex-wrap items-center gap-2 border-t border-zinc-200 pt-2 mt-1" data-testid={`${testId}-founder`}>
    <span className="inline-flex items-center gap-1 font-semibold text-zinc-700"><Award size={12} aria-hidden="true" />Kurucu rozeti</span>
    {current ? <>
      <span className="rounded-full border border-zinc-300 px-2 py-0.5 font-semibold" data-testid={`${testId}-founder-current`}>Kurucu Mekân #{current}</span>
      <input value={reason} onChange={e => setReason(e.target.value)} maxLength={300} placeholder="Geri alma gerekçesi" aria-label="Kurucu rozeti geri alma gerekçesi"
        className="border border-zinc-200 rounded-lg px-2 py-1 text-xs min-w-0" data-testid={`${testId}-founder-reason`} />
      <button type="button" disabled={busy} onClick={revoke} className="px-2.5 py-1 rounded-lg border border-zinc-200 text-zinc-600 disabled:opacity-50" data-testid={`${testId}-founder-revoke`}>Rozeti geri al</button>
    </> : <>
      <select value={choice} onChange={e => setChoice(e.target.value)} disabled={busy || !info} aria-label="Kurucu numarası"
        className="border border-zinc-200 rounded-lg px-2 py-1 text-xs" data-testid={`${testId}-founder-number`}>
        <option value="">Numara seç</option>{(info?.available || []).map(n => <option key={n} value={n}>#{n}</option>)}
      </select>
      <button type="button" disabled={busy || !choice} onClick={assign} className="px-2.5 py-1 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] font-medium disabled:opacity-50" data-testid={`${testId}-founder-assign`}>Kurucu rozeti ver</button>
      <span className="text-zinc-400">{info ? `${info.taken.length}/${info.max} numara kullanımda` : 'Yükleniyor…'}</span>
    </>}
    {error && <p role="alert" className="w-full text-[#9A4F00]" data-testid={`${testId}-founder-error`}>{error}</p>}
  </div>;
};

// Admin: sahibinin planını yönet (PUT /api/admin/users/{id}/plan). onChanged(patch) ile liste anında güncellenir.
// venue: mekân sahibi (12 aylık elle satış, kurucu rozeti). Story özelliği tamamen kaldırıldı (ürün kararı).
export const AdminPlanControl = ({ userId, plan, storedPlan, expiresAt, onChanged, testId, venue = false, canManageProPlus = false, version = null, founderNumber = null, tier = null }) => {
  const [date, setDate] = useState(() => expiresAt ? istanbulDate(expiresAt) : monthsFromToday(12));
  const [indefinite, setIndefinite] = useState(isProPlan(storedPlan) && !expiresAt);
  const [busy, setBusy] = useState(false);
  const [manualSale, setManualSale] = useState(false);
  const isPro = isProPlan(plan);
  const locked = storedPlan === 'pro_plus' && !canManageProPlus;
  const expired = isProPlan(storedPlan) && !isPro;
  // Onaylı organizatörün Pro'su kayıtlı plan değil, onaydan gelir: "Free yap" bir şey değiştirmez, gösterilmez.
  const dynamicPro = tier === 'organizer_pro' && !isProPlan(storedPlan);

  const save = async (nextPlan) => {
    if (locked || nextPlan === 'pro_plus' && !canManageProPlus) return;
    if (!userId) return toast.error('Bu kaydın sahibi yok; plan atanamaz.');
    if (nextPlan === 'pro' && !indefinite && !date) return toast.error('Pro bitiş tarihi zorunludur.');
    setBusy(true);
    try {
      const sale = nextPlan === 'pro' && venue && manualSale;
      const body = {
        plan: nextPlan,
        plan_expires_at: nextPlan === 'pro' && !indefinite ? istanbulEndOfDay(date) : null,
        indefinite: nextPlan === 'pro_plus' || nextPlan === 'pro' && indefinite,
        manual_sale_confirmed: sale,
        ...(sale ? { package: 'venue_paid_12m' } : {}),
      };
      const { data } = await axios.put(`${API}/api/admin/users/${userId}/plan`, body);
      const effective = isProPlan(data.plan) && (!data.plan_expires_at || new Date(data.plan_expires_at) > new Date()) ? data.plan : 'free';
      onChanged({ owner_plan: effective, owner_plan_stored: data.plan, owner_plan_expires_at: data.plan_expires_at });
      setManualSale(false);
      if (nextPlan === 'pro_plus') setIndefinite(true);
      toast.success(data.manual_sale_replayed ? 'Bu satış daha önce kaydedilmiş. Güncel üyelik korundu.'
        : nextPlan === 'pro_plus' ? 'Pro Plus verildi; günlük kullanım hakları sınırsız.' : nextPlan === 'pro' ? (data.manual_delivery ? 'Ücretli mekân üyeliği (12 ay) ve satış kaydı oluşturuldu.' : 'Pro verildi.') : 'Pro kaldırıldı.');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Plan güncellenemedi.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={testId}>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px] ${isPro ? 'bg-[#1E3A8A] text-white' : 'bg-zinc-100 text-zinc-500'}`}
        data-testid={`${testId}-status`}>
        <Crown size={10} strokeWidth={2} /> {isPro ? planLabel(plan) : expired ? `${planLabel(storedPlan)} (süresi doldu)` : 'Free'}
      </span>
      {isPro && <span className="text-zinc-500" data-testid={`${testId}-expiry`}>{tier === 'organizer_pro' && !isProPlan(storedPlan) ? 'Ücretsiz · onaylı organizatör (dinamik)' : expiresAt ? `Bitiş: ${formatPlanDate(expiresAt)}` : 'Süresiz / hediye'}</span>}
      {VERSION_LABELS[version] && <span className="rounded-full border border-zinc-200 px-2 py-0.5 text-zinc-600" data-testid={`${testId}-version`}>{VERSION_LABELS[version]}</span>}
      <span className="text-zinc-400">{venue ? 'Standart paket: 12 ay · 6 aylık paket yeni satışa kapalı' : 'Standart paket: 12 ay'}</span>
      <input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={busy || indefinite || locked}
        className="border border-zinc-200 rounded-lg px-2 py-1 text-xs" aria-label="Pro bitiş tarihi" data-testid={`${testId}-date`} />
      <label className="inline-flex items-center gap-1 text-zinc-500">
        <input type="checkbox" checked={indefinite} onChange={e => { setIndefinite(e.target.checked); if (e.target.checked) setManualSale(false); }} disabled={busy || locked}
          data-testid={`${testId}-indefinite`} />
        Süresiz / hediye
      </label>
      {venue && <label className="inline-flex items-center gap-1 text-zinc-600 w-full">
        <input type="checkbox" checked={manualSale} onChange={e => { setManualSale(e.target.checked); if (e.target.checked) setDate(monthsFromToday(12)); }}
          disabled={busy || indefinite || locked} data-testid={`${testId}-manual-sale`} />
        Tahsilatı dışarıda doğruladım; 12 aylık ücretli mekân satış kaydı oluştur (fiyat ve vergi bu ekranda girilmez)
      </label>}
      <button type="button" onClick={() => save('pro')} disabled={busy || locked}
        className="px-2.5 py-1 rounded-lg bg-[#1E3A8A] text-white font-medium hover:bg-[#16307A] disabled:opacity-50" data-testid={`${testId}-grant`}>
        {plan === 'pro_plus' ? 'Pro’ya geçir' : isPro ? 'Süreyi güncelle' : 'Pro ver'}
      </button>
      {canManageProPlus && plan !== 'pro_plus' && <button type="button" onClick={() => save('pro_plus')} disabled={busy}
        className="px-2.5 py-1 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] font-medium disabled:opacity-50" data-testid={`${testId}-grant-plus`}>Pro Plus ver · Sınırsız</button>}
      {storedPlan === 'pro_plus' && <span className="text-zinc-500">Günlük kullanım sınırsız{locked ? ' · Yalnız ana yönetici değiştirebilir' : ''}</span>}
      {dynamicPro && <span className="text-zinc-500 w-full" data-testid={`${testId}-dynamic-pro-note`}>Bu Pro ücretsiz ve onaya bağlıdır; kaldırmak için profil onayı geri alınır. Kayıtlı plan zaten Free.</span>}
      {(isPro || expired) && !dynamicPro && (
        <button type="button" onClick={() => save('free')} disabled={busy || locked}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50" data-testid={`${testId}-remove`}>
          <X size={11} /> Üyeliği Free yap
        </button>
      )}
      {venue && canManageProPlus && userId && <FounderBadgeControl userId={userId} number={founderNumber} testId={testId} onChanged={onChanged} />}
      {venue && !canManageProPlus && founderNumber && <span className="rounded-full border border-zinc-300 px-2 py-0.5 font-semibold" data-testid={`${testId}-founder-readonly`}>Kurucu Mekân #{founderNumber}</span>}
    </div>
  );
};
